import models from '../fireModels'
import dataTypes from './_dataTypes'
import { createNew, getColsWithAssocs, getColTableMap } from './modelHelper'
import {
  AlgoFacetSettingForColumn,
  ColumnDef,
  EditCallback,
  EditCallbackForColumn,
  ModelDef,
  findAllByArgument,
  AlgoliaSettingsForModel,
  Permission, ModelDatasourceType
} from './index'
import { SearchClient } from 'algoliasearch'
import { SearchOptions } from '@algolia/client-search'
import firebase from 'firebase'
import Firestore = firebase.firestore.Firestore
import { EmbeddableAuth } from './EmbAuth/$embAuth'
import { EmbeddableCommerce } from './EmbCommerce/$embCommerce'
import $algoliasearch from './$algoliasearch'
import {$appHook, genExecutableHookSet} from './$appHook'
import $firestore from './$firestore'

declare interface Window {
  $nuxt: {
    $algoliasearch: SearchClient
    $firestore: Firestore
    $embAuth?: EmbeddableAuth
    $embCommerce?: EmbeddableCommerce
    [_instanceMethod: string]: any
  }
}

/**
 * Vue内で活用するModel定義のClass。
 */

interface SelectionsFunc {
  ($store: any, record?: object, currentValue?: any, initialValue?: any): string[]
}

export interface columnDefinition extends ColumnDef {
  name: string
  searchable?: true
  selections?: SelectionsFunc
  order?: number
}

const DefaultPermission: Permission = {
  create: true,
  read: true,
  update: true,
  delete: true,
} as const
export const getDefaultPermission = () => {
  return Object.assign({}, DefaultPermission)
}
export const sanitizePermission = (permission: Permission): Permission => {
  return {
    create: permission.create === false ? false : true,
    read: permission.read === false ? false : true,
    update: permission.update === false ? false : true,
    delete: permission.delete === false ? false : true,
  }
}

export class ModelFactory implements ModelDef {
  datasource: ModelDatasourceType
  primaryKeyCol: columnDefinition
  defaultSort: { key: string; order: string }
  colNames: string[]
  colLabels: { [colName: string]: string }
  colOrders: string[]
  tableName: string
  tableLabel: string
  tableComment: string
  associations: object
  materials: object
  useAlgolia: boolean
  doNotSyncToAlgolia: boolean // todo: 消したい, useAlgolia とduplicatedであるため
  useAlgoliaOnIndexView?: false
  columns: { [colName: string]: columnDefinition }
  viewTable: boolean
  editable: boolean
  deletable: boolean
  timestamps: boolean
  cameledName: string
  createNew: Function
  getColsWithAssocs: Function
  getColTableMap: Function
  editCallbacksByColname: { [colName: string]: EditCallbackForColumn }
  editCallback: EditCallback
  facetColumns: {
    [colName: string]: AlgoFacetSettingForColumn
  }
  modelMethods: { [methodName: string]: Function }
  shouldCreateHistoryData
  algoSettings?: AlgoliaSettingsForModel
  /**
   * Overrides, find系の挙動を書き換えをする
   */
  overrides?: {
    findAll?: Function
    findAllBy?: Function
    findById?: Function
    findAllByIds?: Function
  }
  // overrides
  onSubmitFunction?: (data) => Promise<any> | any
  // overrides
  onDeleteFunction?: (data) => Promise<any> | any

  constructor(tableName, modelDefinition: ModelDef = null) {
    $appHook.callHooksNonAsync(ModelFactory.hooks.init.before, {tableName, modelDefinition}, this)
    $appHook.callHooksNonAsync(ModelFactory.hooks.byModelName(tableName).init.before, {tableName, modelDefinition}, this)
    let model = null
    if (modelDefinition) {
      model = modelDefinition
    } else {
      model = models[tableName]
        ? typeof models[tableName] === 'function'
          ? models[tableName]({}, dataTypes)
          : models[tableName]
        : null
    }
    if (!model) {
      return
    }
    this.colOrders = []
    Object.keys(model).forEach((key) => {
      // @ts-ignore
      this[key] = model[key]
    })
    // Set shortcut params
    this.colNames = []
    this.colLabels = {}
    this.editCallbacksByColname = {}
    this.facetColumns = {}
    this.modelMethods = {}
    Object.keys(this.columns)
      .sort((colName1: string, colName2: string) => this.colOrders.indexOf(colName1) - this.colOrders.indexOf(colName2))
      .map((colName: string) => {
        let col = this.columns[colName]
        col.name = colName
        this.colNames.push(colName)
        this.colLabels[colName] = col.label || colName
        // Primary Keys
        if (col.primaryKey === true) {
          this.primaryKeyCol = {
            name: colName,
            ...col,
          }
        }
        // editCallback
        if (typeof col.editCallback === 'function') {
          this.editCallbacksByColname[colName] = col.editCallback
        }

        // Faceting for Algolia search
        if (col.facet) {
          this.facetColumns[colName] = col.facet
        }

        // Configure REFERENCE
        // referenceの場合は、そのRef値を保持するカラムを追加する
        if (col.type === 'REFERENCE') {
          if (
            !col.referenceOfStore ||
            !col.referenceOfStore.storeName ||
            !col.referenceOfStore.label ||
            !col.referenceOfStore.key
          ) {
            throw new Error(
              `[lib/ModelFactory.ts] tableName: ${this.tableName} of colName: ${colName}, type: 'REFERENCE' need to has correct referenceOfStore.key, label, storeName`,
            )
          }
          let refLabelColName =
            col.referenceOfStore.refLabelColName || `${col.referenceOfStore.storeName}_${col.referenceOfStore.label}_`
          let func = null
          if (!this.columns[refLabelColName]) {
            this.columns[refLabelColName] = {
              type: 'STRING',
              name: refLabelColName,
              label: refLabelColName.replace(/_*$/, ''),
              editable: false,
              inputAttrs: { placeholder: '(自動算出)' },
            }
            this.colNames.push(refLabelColName)
            this.colLabels[colName] = refLabelColName
            // Add autoCalc
            // if(this.editCallbacksByColname[colName]) {
            //   func = this.editCallbacksByColname[colName]
            // }
          }
          this.editCallbacksByColname[colName] = async ({ row, newValue, oldValue, isNewRecord }) => {
            if (newValue && newValue !== oldValue) {
              try {
                const $data = await window.$nuxt.$models[col.referenceOfStore.storeName].findById(newValue)
                if ($data) {
                  row[refLabelColName] =
                    typeof col.referenceOfStore.labelFormatter === 'function'
                      ? col.referenceOfStore.labelFormatter($data)
                      : $data[col.referenceOfStore.label]
                }
              } catch (e) {
                console.error(e)
                console.error({ row, newValue, oldValue })
              }
            }
            if (col.editCallback) {
              row = col.editCallback({ row, newValue, oldValue, isNewRecord })
            }
            return row
          }
        }
        // End of Configure REFERENCE
      })
    this.editCallback = async ({ row, key, newValue, oldValue, isNewRecord }) => {
      if (newValue === oldValue && !isNewRecord) {
        return row
      }
      // Auto calc 関連
      if (this.editCallbacksByColname[key]) {
        row = await this.editCallbacksByColname[key]({
          row,
          newValue,
          oldValue,
          isNewRecord,
        })
      }
      return row
    }

    if (this.timestamps !== false && !this.columns.作成日時) {
      this.columns.作成日時 = {
        name: '作成日時',
        type: 'STRING',
        editable: false,
        inputAttrs: { disabled: true, placeholder: '(自動算出)' },
      }
    }
    if (this.timestamps !== false && !this.columns.更新日時) {
      this.columns.更新日時 = {
        name: '更新日時',
        type: 'STRING',
        editable: false,
        inputAttrs: { disabled: true, placeholder: '(自動算出)' },
      }
    }

    // Methods
    this.createNew = () => {
      return createNew(this)
    }

    this.getColsWithAssocs = (models, modelAssociationDefs = null) => {
      return getColsWithAssocs(this, models, modelAssociationDefs || this.associations)
    }

    this.getColTableMap = (models, modelAssociationDefs = null) => {
      return getColTableMap(this, modelAssociationDefs || this.associations)
    }

    if (model.modelMethods) {
      this.modelMethods = model.modelMethods
    }

    if (model.shouldCreateHistoryData) {
      this.shouldCreateHistoryData = model.shouldCreateHistoryData
    }

    /**
     * AlgoliaをModel全体として利用するかどうか
     * 20210521時点: Default true だが、default false に持っていきたい
     */
    this.useAlgolia = model.useAlgolia !== false
    if(this.useAlgolia === false) {
      this.doNotSyncToAlgolia = false
      this.useAlgoliaOnIndexView = false
    }
    $appHook.callHooksNonAsync(ModelFactory.hooks.init.after, {tableName, modelDefinition}, this)
    $appHook.callHooksNonAsync(ModelFactory.hooks.byModelName(tableName).init.after, {tableName, modelDefinition}, this)
  }

  static get hooks() {
    return {
      init: {
        after: '$CORE.ModelFactory.init.after',
        before: '$CORE.ModelFactory.init.before',
      },
      byDatasource(datasourceName: ModelDatasourceType) {
        return {
          find: genExecutableHookSet(`$CORE.ModelFactory.datasource:${datasourceName}.find`),
        }
      },
      byModelName(modelName: string) {
        return {
          init: {
            after: `$CORE.ModelFactory.${modelName}.init.after`,
            before: `$CORE.ModelFactory.${modelName}.init.before`,
          },
          find: genExecutableHookSet('$CORE.ModelFactory.find'),
        }
      }
    }
  }

  get $firestore() {
    return window.$nuxt && window.$nuxt.$firestore ? window.$nuxt.$firestore : $firestore
  }
  get collectionRef() {
    return this.$firestore.collection(this.tableName)
  }

  /**
   *
   * @param id
   * overrideable
   */
  async findById(id) {
    if(this.overrides && this.overrides.findById) {
      return this.overrides.findById(id)
    }
    const doc = await this.collectionRef.doc(`${id}`).get()
    return doc.exists ? { ...doc.data(), id: doc.id } : null
  }

  async findByIdFromAlgo(id) {
    try {
      const doc = await this.algoIndex.getObject(id)
      // @ts-ignore
      doc.id = id
      return doc
    } catch (e) {
      // algoIndex.getObject can throw 404 Error
      return null
    }
  }

  /**
   * TODO: V3ではパラメータを調整したい。(複合検索対応)
   * @param query
   * @param optionsOfAlgoliaSearch
   */
  async findAll(query = '', optionsOfAlgoliaSearch: SearchOptions = {}) {
    if(this.overrides && this.overrides.findAll) {
      return this.overrides.findAll(query = '', optionsOfAlgoliaSearch)
    }
    /**
     * datasource が指定されていれば、プラグイン側の挙動に頼っていく ($appHook経由)
     */
    if(this.datasource) {
      // model名でのhook登録があれば
      const findHookNameByModel = ModelFactory.hooks.byModelName(this.tableName).find.DO
      const findHookNameByDatasource = ModelFactory.hooks.byDatasource(this.datasource).find.DO
      const targetHookName = $appHook.hasHook(findHookNameByModel) ? findHookNameByModel : ($appHook.hasHook(findHookNameByDatasource) ? findHookNameByDatasource : null)
      return $appHook.doExecutableHook({
        callerContext: this,
        currentValue: [],
        hookBaseName: targetHookName,
        additionalArgs: {query, optionsOfAlgoliaSearch}
      })
    }
    return this.__defaultBehaviorOfFindAll(query, optionsOfAlgoliaSearch)
  }

  /**
   * V3では外出ししたい, findAllの挙動
   * 一旦Migrationのためにここに切り出しているが、分割したい
   * @private
   */
  async __defaultBehaviorOfFindAll(query = '', optionsOfAlgoliaSearch: SearchOptions = {}) {
    // 全件取得 (1000件がリミットのため、数回繰り返す)
    const option: any = Object.assign({}, optionsOfAlgoliaSearch)
    const index = this.algoIndex
    let hits = []
    await index.browseObjects({
      query: query, // Empty query will match all records
      filters: option.filters,
      batch: (batch) => {
        hits = hits.concat(batch.map(d => {
          if(!d.id) {
            d.id = d.objectID
          }
          return d
        }))
      },
    })
    return hits
  }

  async findAllWithoutBrowse(query = '', optionsOfAlgoliaSearch: any = {}) {
    if (!optionsOfAlgoliaSearch.hitsPerPage) {
      optionsOfAlgoliaSearch.hitsPerPage = 1000
    }
    return (await this.algoIndex.search(query, optionsOfAlgoliaSearch)).hits
  }

  get algoIndex() {
    return (window.$nuxt && window.$nuxt.$algoliasearch ? window.$nuxt.$algoliasearch : $algoliasearch).initIndex(this.tableName)
  }

  async findAllWithPagination(query = '', optionsOfAlgoliaSearch: SearchOptions = {}) {
    // 全件取得 (1000件がリミットのため、数回繰り返す)
    const options: any = Object.assign({}, optionsOfAlgoliaSearch, {
      hitsPerPage: 1000,
      page: 0,
    })
    const index = this.algoIndex
    let hits = []
    const firstResult = await index.search(query, options)
    if (firstResult.nbPages === 1) {
      return firstResult.hits
    }

    return hits
  }

  async findAllBy(filters: findAllByArgument, optionsOfAlgoliaSearch: SearchOptions = {}) {
    /**
     * Model定義上の上書き挙動
     */
    if(this.overrides && this.overrides.findAllBy) {
      return this.overrides.findAllBy(filters, optionsOfAlgoliaSearch)
    }
    if (typeof filters === 'function') {
      return (await this.findAll('', optionsOfAlgoliaSearch)).filter(filters as any)
    }
    const option: any = optionsOfAlgoliaSearch

    option.filters = this._formatObjectFiltersExpressionForAlgo(filters)
    console.log(`findAllBy, option.filters:`, option.filters)
    return option.filters ? await this.findAll('', option) : []
  }

  async countBy(filters: string): Promise<number> {
    filters = this._formatObjectFiltersExpressionForAlgo(filters)
    return (await this.algoIndex.search('', { filters })).nbHits
  }
  async findAllByIds(ids: string[]) {
    if(this.overrides && this.overrides.findAllByIds) {
      return this.overrides.findAllByIds(ids)
    }
    return (await this.algoIndex.getObjects(ids)).results
  }

  async findAllFromFirebase(where, limit: number = null, orderBy:[string, 'desc' | 'asc'] = null, paged = 1): Promise<any[]> {
    let collectionRef = this.collectionRef
    if (where) {
      if (!Array.isArray(where)) {
        where = Object.keys(where).map((k: string) => [k, '==', where[k]])
      }
      for (let i = 0; i < where.length; i++) {
        // @ts-ignore
        collectionRef = collectionRef.where(where[i][0], where[i][1], where[i][2])
      }
    }
    if(orderBy && orderBy[0] && orderBy[1]) {
      // @ts-ignore
      collectionRef = collectionRef.orderBy(orderBy[0], orderBy[1])
    }
    if(limit) {
      // @ts-ignore
      collectionRef = collectionRef.limit(limit * paged)
    }
    const res = (await collectionRef.get()).docs.map((r) => ({
      ...r.data(),
      id: r.id,
    }))
    // TODO: sliceしてしまっているのをやめたい
    if (limit && paged) {
      return res.slice(limit * (paged - 1), limit * paged)
    } else {
      return res
    }
  }

  async findAllFromAlgolia(where = null, fetchFromLocal = true, { attributesToRetrieve = null }) {}

  async fetchFacets(
    facets: string[] = ['*'],
    filters: findAllByArgument = '',
  ): Promise<{ [colName: string]: { [valueName: string]: number } }> {
    const searchOptions = { facets, length: 1, offset: 0, filters: '' }
    if (filters) {
      searchOptions.filters = this._formatObjectFiltersExpressionForAlgo(filters)
    }
    const res = await this.algoIndex.search('', searchOptions)
    return res.facets
  }

  async fetchFacetNamesByColName(colName: string, filters: findAllByArgument = ''): Promise<string[]> {
    const facets = await this.fetchFacets([colName], filters)
    return Object.keys(facets[colName] || {}) as string[]
  }

  _formatObjectFiltersExpressionForAlgo(filters: findAllByArgument): string {
    if (typeof filters === 'string') {
      return filters
    }

    // https://www.algolia.com/doc/api-reference/api-parameters/filters/#apply-filters-on-a-search-query
    // NOTE: The attr have to be registered as Facet on Algolia indexing
    return Object.keys(filters)
      // @ts-ignore
      .reduce((res: any[], r:string) => {
        if (this.facetColumns[r]) {
          const filterKey = r.indexOf('"') >= 0 ? `"${r.replace('"', '\\\\"')}"` : r
          const filterValue = typeof filters[r] === 'string' ? `"${filters[r].replace('"', '\\\\"')}"` : filters[r]
          res.push(`${filterKey}:${filterValue}`)
        }
        return res
      }, [])
      // @ts-ignore
      .join(' AND ')
  }
}

export default ModelFactory
